// The info button in the top bar: did the last push land? (O2, B-112.)
//
// A DEVELOPMENT AID, for the product owner while the site is built; it comes
// out before the site goes public.
//
// ALWAYS THERE. During development it is a visible, always-available info
// icon in the top bar from a tablet up, drawn for everybody the top bar is
// drawn for: signed in, signed out, on the tour, and on the front page beside
// Sign in. A phone's bar has no room for it beside the week, so there it is
// the account menu's *About this build*, which opens the same lines in a
// sheet over the screen (B-276). `/about` stays, for a deploy nobody can sign
// in to, and keeps the long answer.
//
// AND SHORT: the last-updated time and the build version are enough to know
// a push landed. So the window says when this build was made and which
// commit it is. The API's half is one red line, and only when it is not
// answering: a push can load the page over Functions that do not
// answer (2026-09-21), and that is the one thing besides the date a push
// working means.
//
// A POPOVER UNDER THE ICON, not a sheet: two lines in a full-height drawer
// read as a screen with nothing on it. The API is asked when it opens, not
// when the bar draws: Mantine's popover mounts its dropdown only while open.

import { buildInfo, shortSha } from '../buildInfo';
import { Popover, Sheet } from '../kit/overlays';
import { Heading, Stack, Text } from '../kit/ui';
import { ago, useApiAnswer, when } from '../screens/AboutScreen';
import '../styles/screens/account.css';

export function AboutButton() {
  return (
    <Popover width={260} button={{ icon: 'info', label: 'About this build', variant: 'secondary' }}>
      <AboutShort />
    </Popover>
  );
}

/**
 * The same lines in a sheet, for a phone's menu (B-276): over the screen it was
 * opened from, so closing it leaves you where you were. `/about` would have
 * been a page outside the app's frame with no way back in an installed app.
 */
export function AboutSheet({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  return (
    <Sheet opened={opened} onClose={onClose} height={300}>
      <AboutShort />
    </Sheet>
  );
}

function AboutShort() {
  const build = buildInfo();
  const api = useApiAnswer();
  return (
    <div className="fsp-about-short">
      <Stack gap={3}>
        <Heading>About this build</Heading>
        <div className="fsp-about-row">
          <Text tone="secondary">Updated</Text>
          <div className="fsp-about-value">
            <div>{when(build.builtAt)}</div>
            <Text size="xs" tone="secondary" block>
              {ago(build.builtAt)}
            </Text>
          </div>
        </div>
        <div className="fsp-about-row">
          <Text tone="secondary">Version</Text>
          <div className="fsp-about-value">
            <Text mono>{shortSha(build.sha)}</Text>
          </div>
        </div>
        {api.state === 'failed' ? (
          <Text tone="bad" block>
            The site loaded, but the API is not answering.
          </Text>
        ) : null}
      </Stack>
    </div>
  );
}
